/**
 * Affiliate discount confirmation. Self-gating: inert unless a visitor
 * arrives with a known ?aff= flag (or the discount is live in the cart).
 *
 * Layer 1 — capture the ?aff= flag first-party (localStorage + cookie, 30-day TTL)
 * Layer 2 — once-per-session welcome toast (snippets/affiliate-welcome.liquid)
 * Layer 3 — announcement-bar badge (#affiliate-utility-badge placeholder)
 * Layer 4 — cart-drawer line driven by live /cart.js state, independent of the flag
 *
 * Adding an affiliate = one AFFILIATES entry.
 */
(function () {
  'use strict';

  const AFFILIATES = {
    pnwops: { name: 'PNW Outdoor Pursuits', code: 'PNWOPS', pct: 10 },
  };

  const STORAGE_KEY = 'prolite:affiliate';
  const COOKIE_NAME = 'prolite_aff';
  const WELCOMED_KEY = 'prolite:aff-welcomed';
  const TTL_MS = 30 * 24 * 60 * 60 * 1000;

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeFlag(handle) {
    const record = JSON.stringify({ handle: handle, ts: Date.now() });
    try {
      localStorage.setItem(STORAGE_KEY, record);
    } catch (e) {
      // storage unavailable; cookie below may still work
    }
    try {
      document.cookie =
        COOKIE_NAME +
        '=' +
        encodeURIComponent(record) +
        '; max-age=' +
        Math.floor(TTL_MS / 1000) +
        '; path=/; SameSite=Lax; Secure';
    } catch (e) {
      // ignore
    }
  }

  function clearFlag() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    try {
      document.cookie = COOKIE_NAME + '=; max-age=0; path=/; SameSite=Lax; Secure';
    } catch (e) {
      // ignore
    }
  }

  function readFlag() {
    let raw = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    if (!raw) raw = readCookie(COOKIE_NAME);
    if (!raw) return null;

    let record;
    try {
      record = JSON.parse(raw);
    } catch (e) {
      clearFlag();
      return null;
    }
    if (!record || !AFFILIATES[record.handle] || !record.ts || Date.now() - record.ts > TTL_MS) {
      clearFlag();
      return null;
    }
    return record.handle;
  }

  /* Layer 1 — capture ?aff= and strip it from the URL (valid or not) */
  function captureFlag() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('aff')) return;

    const handle = (params.get('aff') || '').trim().toLowerCase();
    if (AFFILIATES[handle]) writeFlag(handle);

    params.delete('aff');
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    try {
      history.replaceState(null, '', cleanUrl);
    } catch (e) {
      // ignore
    }
  }

  /* Layer 2 — once-per-session welcome toast */
  function showToast(affiliate) {
    const toast = document.getElementById('affiliate-welcome');
    if (!toast) return;
    try {
      if (sessionStorage.getItem(WELCOMED_KEY)) return;
      sessionStorage.setItem(WELCOMED_KEY, '1');
    } catch (e) {
      return;
    }

    const nameEl = toast.querySelector('[data-affiliate-name]');
    const pctEl = toast.querySelector('[data-affiliate-pct]');
    if (nameEl) nameEl.textContent = affiliate.name;
    if (pctEl) pctEl.textContent = affiliate.pct;

    toast.removeAttribute('hidden');
    // Force a reflow so the browser registers the hidden start state before we
    // flip .active — guarantees the transition fires (double-rAF is unreliable here).
    void toast.offsetWidth;
    toast.classList.add('active');

    function close() {
      toast.classList.remove('active');
      setTimeout(function () {
        toast.setAttribute('hidden', '');
      }, 400);
    }

    const closeButton = toast.querySelector('.affiliate-toast__close');
    if (closeButton) closeButton.addEventListener('click', close);
    toast.addEventListener('keyup', function (event) {
      if (event.key === 'Escape') close();
    });
  }

  /* Layer 3 — announcement-bar badge */
  function showBadge(affiliate) {
    const badge = document.getElementById('affiliate-utility-badge');
    if (!badge) return;
    badge.textContent = affiliate.pct + '% ' + affiliate.name + ' discount active';
    badge.removeAttribute('hidden');
  }

  /* Layer 4 — reinforce the real applied discount wherever the customer sees the
     cart (cart page footer and, if the theme is switched to a drawer, the drawer).
     Reads live /cart.js state, independent of the aff flag. */
  function affiliateByCode(code) {
    const lower = String(code).toLowerCase();
    const handle = Object.keys(AFFILIATES).find(function (key) {
      return AFFILIATES[key].code.toLowerCase() === lower;
    });
    return handle ? AFFILIATES[handle] : null;
  }

  function watchedCodes() {
    const handle = readFlag();
    if (handle) return [AFFILIATES[handle].code];
    return Object.keys(AFFILIATES).map(function (key) {
      return AFFILIATES[key].code;
    });
  }

  function populateLine(line, cart) {
    function hide() {
      line.textContent = '';
      line.setAttribute('hidden', '');
    }

    if (!cart || !cart.item_count) return hide();

    const codes = watchedCodes();
    const appliedTitles = (cart.cart_level_discount_applications || []).map(function (discount) {
      return String(discount.title || '').toLowerCase();
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        if (allocation.discount_application) {
          appliedTitles.push(String(allocation.discount_application.title || '').toLowerCase());
        }
      });
    });

    const appliedCode = codes.find(function (code) {
      return appliedTitles.indexOf(code.toLowerCase()) !== -1;
    });

    if (appliedCode) {
      // The native cart-level discount list (sibling ul) is the source of truth
      // when it already names the code — never show a duplicate line.
      const nativeList = line.parentElement ? line.parentElement.querySelector('ul.discounts') : null;
      if (nativeList && nativeList.textContent.toLowerCase().indexOf(appliedCode.toLowerCase()) !== -1) {
        return hide();
      }
      const affiliate = affiliateByCode(appliedCode);
      line.textContent = affiliate
        ? '−' + affiliate.pct + '% applied (' + appliedCode + ')'
        : 'Discount applied (' + appliedCode + ')';
      line.removeAttribute('hidden');
      return;
    }

    // Not allocated in the cart yet — promise auto-apply only when Shopify's own
    // signals say the code is attached (cart discount_codes or discount_code cookie).
    const codeEntry = (cart.discount_codes || []).find(function (entry) {
      return (
        entry.applicable !== false &&
        codes.some(function (code) {
          return code.toLowerCase() === String(entry.code).toLowerCase();
        })
      );
    });
    const cookieValue = readCookie('discount_code');
    const pendingCode =
      (codeEntry && String(codeEntry.code)) ||
      (cookieValue &&
        codes.find(function (code) {
          return code.toLowerCase() === cookieValue.toLowerCase();
        }));

    if (pendingCode) {
      const affiliate = affiliateByCode(pendingCode);
      line.textContent =
        pendingCode.toUpperCase() +
        (affiliate ? ' — ' + affiliate.pct + '%' : '') +
        ' will be applied at checkout';
      line.removeAttribute('hidden');
      return;
    }

    hide();
  }

  function updateCartLines() {
    const lines = document.querySelectorAll('.affiliate-cart-line');
    if (!lines.length) return;

    const cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js';
    fetch(cartUrl)
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        // Re-query: a section may have re-rendered while the fetch was in flight.
        document.querySelectorAll('.affiliate-cart-line').forEach(function (line) {
          populateLine(line, cart);
        });
      })
      .catch(function () {
        // network hiccup: leave the lines as-is
      });
  }

  let cartUpdateTimer = null;
  function scheduleCartUpdate() {
    clearTimeout(cartUpdateTimer);
    cartUpdateTimer = setTimeout(updateCartLines, 100);
  }

  function initCartWatch() {
    if (!document.querySelector('.affiliate-cart-line')) return;

    // cartUpdate covers quantity changes on the cart page and the drawer (cart.js
    // publishes it after replacing .js-contents innerHTML).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleCartUpdate);
    }

    // The drawer's innerHTML is wholesale replaced on add-to-cart and the
    // cartUpdate publish order differs by path — observe it directly as a
    // belt-and-suspenders trigger (no-op on stores without a drawer).
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const observer = new MutationObserver(function (mutations) {
        const external = mutations.some(function (mutation) {
          const target = mutation.target;
          return !(
            (target.classList && target.classList.contains('affiliate-cart-line')) ||
            (target.closest && target.closest('.affiliate-cart-line'))
          );
        });
        if (external) scheduleCartUpdate();
      });
      observer.observe(drawer, { childList: true, subtree: true });
    }

    scheduleCartUpdate();
  }

  function init() {
    captureFlag();
    const handle = readFlag();
    if (handle) {
      showBadge(AFFILIATES[handle]);
      showToast(AFFILIATES[handle]);
    }
    initCartWatch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
